import { useEffect, useRef, useState, useMemo } from "react";
import Model from "@tripian/model";
import { api } from "@tripian/core";
import moment from "moment";

export interface UseProgressiveTourApiParams {
  cityId?: string;
  providerId?: string;
  lat?: number;
  lng?: number;
  date?: string;
  to?: string;
  adults?: number;
  currency?: string;
  lang?: string;
  radius?: number;
  keywords?: string;
  categories?: string;
  sortingBy?: string;
  sortingType?: string;
  currentPage: number; // 1-based
  pageSize?: number; // default: 10
  instantAvailability?: number; // 0 or 1 — defaults to window.tconfig.TOUR_INSTANT_AVAILABILITY
}

const useProgressiveTourApi = (params: UseProgressiveTourApiParams) => {
  const {
    cityId,
    providerId,
    lat,
    lng,
    date,
    to,
    adults = 2,
    currency = "usd",
    lang = "en",
    radius = 10,
    keywords,
    categories,
    sortingBy,
    sortingType,
    currentPage,
    pageSize = 10,
    instantAvailability,
  } = params;

  const resolvedInstantAvailability =
    instantAvailability !== undefined
      ? instantAvailability
      : window.tconfig?.TOUR_INSTANT_AVAILABILITY
      ? 1
      : 0;

  // When instant availability is active, API enforces a max of 10 results per page
  const effectiveLimit = resolvedInstantAvailability === 1 ? Math.min(pageSize, 10) : pageSize;

  const isActive =
    typeof window !== "undefined" &&
    window.tconfig &&
    window.tconfig.SHOW_TOURS_AND_TICKETS &&
    providerId !== undefined;

  const [allTours, setAllTours] = useState<Model.TourSearchProduct[]>([]);
  const [nextOffset, setNextOffset] = useState<number>(0);
  const [isExhausted, setIsExhausted] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchingRef = useRef<boolean>(false);
  const generationRef = useRef<number>(0);

  // Search key — when search parameters change, reset everything
  const searchKey = useMemo(
    () =>
      JSON.stringify({
        cityId,
        providerId,
        lat,
        lng,
        date,
        to,
        adults,
        currency,
        lang,
        radius,
        keywords,
        categories,
        sortingBy,
        sortingType,
      }),
    [cityId, providerId, lat, lng, date, to, adults, currency, lang, radius, keywords, categories, sortingBy, sortingType]
  );

  // Reset when search parameters change
  useEffect(() => {
    generationRef.current += 1;
    setAllTours([]);
    setNextOffset(0);
    setIsExhausted(false);
    setLoading(false);
    fetchingRef.current = false;
  }, [searchKey]);

  // Auto-fetch loop
  useEffect(() => {
    if (!isActive || !cityId || !providerId || lat === undefined || lng === undefined) {
      return;
    }

    const neededItems = currentPage * pageSize;

    if (isExhausted || allTours.length >= neededItems) {
      return;
    }

    if (fetchingRef.current) {
      return;
    }

    const currentGeneration = generationRef.current;
    fetchingRef.current = true;
    setLoading(true);

    const today = moment().startOf("day");
    const clampedDate =
      date && moment(date).isBefore(today)
        ? today.format("YYYY-MM-DD")
        : date || today.format("YYYY-MM-DD");

    const request: Partial<Model.TourSearchRequest> = {
      cityId,
      providerId,
      lat,
      lng,
      date: clampedDate,
      adults,
      currency,
      limit: effectiveLimit,
      offset: nextOffset,
      lang,
      radius,
      instantAvailability: resolvedInstantAvailability,
      ...(to ? { to } : {}),
      ...(keywords ? { keywords } : {}),
      ...(categories ? { categories } : {}),
      ...(sortingBy ? { sortingBy } : {}),
      ...(sortingType ? { sortingType } : {}),
    } as Model.TourSearchRequest;

    const attemptFetch = (retryCount: number): void => {
      api
        .tourSearch(request as Model.TourSearchRequest)
        .then((result) => {
          if (currentGeneration !== generationRef.current) {
            return;
          }

          const products = result.products || [];

          if (products.length === 0) {
            setIsExhausted(true);
          } else {
            setAllTours((prev) => [...prev, ...products]);
            setNextOffset((prev) => prev + products.length);
            if (products.length < effectiveLimit) {
              setIsExhausted(true);
            }
          }

          fetchingRef.current = false;
          setLoading(false);
        })
        .catch((err: any) => {
          if (currentGeneration !== generationRef.current) {
            fetchingRef.current = false;
            setLoading(false);
            return;
          }

          if (retryCount < 1) {
            console.warn(`Progressive Tour Search failed, retrying (${retryCount + 1}/1)...`);
            setTimeout(() => attemptFetch(retryCount + 1), 2000);
            return;
          }

          console.error("Progressive Tour Search Error:", err);
          setIsExhausted(true);
          fetchingRef.current = false;
          setLoading(false);
        });
    };

    attemptFetch(0);
  }, [
    isActive,
    cityId,
    providerId,
    lat,
    lng,
    date,
    to,
    adults,
    currency,
    lang,
    radius,
    keywords,
    categories,
    sortingBy,
    sortingType,
    currentPage,
    pageSize,
    effectiveLimit,
    resolvedInstantAvailability,
    nextOffset,
    isExhausted,
    allTours.length,
  ]);

  // Compute the current page slice
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = currentPage * pageSize;
  const tours = allTours.slice(startIndex, endIndex);

  const totalLoaded = allTours.length;

  // hasNextPage: either more items already loaded beyond current page, or not exhausted yet
  const hasNextPage = !isExhausted || allTours.length > endIndex;

  // totalPages is only known when exhausted
  const totalPages = isExhausted ? Math.ceil(allTours.length / pageSize) : undefined;

  // loading for current page: page not full yet and still fetching
  const pageLoading = loading || (!isExhausted && allTours.length < endIndex);

  return {
    tours,
    allTours,
    loading: pageLoading,
    hasNextPage,
    totalLoaded,
    isExhausted,
    totalPages,
  };
};

export default useProgressiveTourApi;
